import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import NotFoundPage from "./pages/NotFoundPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Navbar } from "./components/Navbar";
import { HeaderBanner } from "./components/HeaderBanner";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { SocialProof } from "./components/SocialProof";
import AdminSocialProof from "./pages/AdminSocialProof";
import Landing from "./pages/Landing";
import PricingPage from "./pages/PricingPage";
import AIToolsPage from "./pages/AIToolsPage";
import SitemapPage from "./pages/SitemapPage";
import PartnersPage from "./pages/PartnersPage";
import EnterprisePage from "./pages/EnterprisePage";
import ReferFriendPage from "./pages/ReferFriendPage";
import TrainingPage from "./pages/TrainingPage";
import AdminEbooks from "./pages/AdminEbooks";
import BecomePartnerPage from "./pages/BecomePartnerPage";
import PartnerTermsPage from "./pages/PartnerTermsPage";
import AdminPartners from "./pages/AdminPartners";
import AdminMembers from "./pages/AdminMembers";import AdminOrganisations from "./pages/AdminOrganisations";
import AdminNdas from "./pages/AdminNdas";
import AdminInvoices from "./pages/AdminInvoices";
import AdminAbandoned from "./pages/AdminAbandoned";
import SignNda from "./pages/SignNda";
import PromptQueenTips from "./pages/PromptQueenTips";
import CategoriesPage from "./pages/CategoriesPage";
import IndustryLanding from "./pages/IndustryLanding";
import CommandPage from "./pages/CommandPage";
import StartTrialPage from "./pages/StartTrialPage";
import Dictionary from "./pages/Dictionary";
import Account from "./pages/Account";
import { Login, Register, ForgotPassword, ResetPassword, ChangePassword, VerifyEmail } from "./pages/AuthPages";
import { Checkout, PaymentSuccess, PaymentCancel } from "./pages/CheckoutPages";
import { Favourites, Recent, CheatSheet } from "./pages/MemberPages";
import { InfoPage, FaqPage, Blog, BlogPost } from "./pages/InfoPages";
import BlogSubscriptionPage from "./pages/BlogSubscriptionPage";
import AboutPage from "./pages/AboutPage";
import HelpPage from "./pages/HelpPage";
import { AdminBlog } from "./pages/AdminBlog";
import AdminBlogSubscribers from "./pages/AdminBlogSubscribers";
import { AdminIndustries } from "./pages/AdminIndustries";
import { AdminVouchers } from "./pages/AdminVouchers";
import AdminLogin from "./pages/AdminLogin";
import AdminShowcase from "./pages/AdminShowcase";
import AdminVideos from "./pages/AdminVideos";
import AdminProspects from "./pages/AdminProspects";
import { PortalLogin, PortalLayout, PortalDashboard, PortalDeals, AdminSalesPricing } from "./pages/SalesPortal";
import { AdminLayout, AdminAnalytics, AdminSearches, AdminCommands, AdminCategories, AdminSettings, AdminBroadcast, AdminSecurity } from "./pages/AdminPages";

const Loading = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>
);

const MemberRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (user.membership === "free" && user.role !== "admin") return <Navigate to="/pricing" replace />;
  return children;
};

// Route that requires ANY logged-in user (free, trial, paid, admin) — used for Account.
const AuthRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  return children;
};

const PortalRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/portal/login" replace />;
  if (user.role !== "sales_consultant" && user.role !== "admin") return <Navigate to="/portal/login" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <HeaderBanner />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/ai-tools" element={<AIToolsPage />} />
                <Route path="/sitemap" element={<SitemapPage />} />
                <Route path="/enterprise" element={<EnterprisePage />} />
                <Route path="/refer-a-friend" element={<ReferFriendPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/become-a-partner" element={<BecomePartnerPage />} />
                <Route path="/partner-terms" element={<PartnerTermsPage />} />
                <Route path="/industry/:slug" element={<IndustryLanding />} />
                <Route path="/command/:slug" element={<CommandPage />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/blog-updates/confirm" element={<BlogSubscriptionPage key="confirm" />} />
                <Route path="/blog-updates/unsubscribe" element={<BlogSubscriptionPage key="unsubscribe" />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/:slug" element={<InfoPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/start-trial" element={<StartTrialPage />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/sign-nda" element={<SignNda />} />
                <Route path="/prompt-queen-tips" element={<PromptQueenTips />} />
                <Route path="/checkout/:planKey" element={<Checkout />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/app" element={<MemberRoute><Dictionary /></MemberRoute>} />
                <Route path="/training" element={<MemberRoute><TrainingPage /></MemberRoute>} />
                <Route path="/app/favourites" element={<MemberRoute><Favourites /></MemberRoute>} />
                <Route path="/app/recent" element={<MemberRoute><Recent /></MemberRoute>} />
                <Route path="/app/cheatsheet" element={<MemberRoute><CheatSheet /></MemberRoute>} />
                <Route path="/app/account" element={<AuthRoute><Account /></AuthRoute>} />
                <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route index element={<AdminAnalytics />} />
                  <Route path="searches" element={<AdminSearches />} />
                  <Route path="commands" element={<AdminCommands />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="industries" element={<AdminIndustries />} />
                  <Route path="vouchers" element={<AdminVouchers />} />
                  <Route path="social-proof" element={<AdminSocialProof />} />
                  <Route path="broadcast" element={<AdminBroadcast />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="blog-subscribers" element={<AdminBlogSubscribers />} />
                  <Route path="partners" element={<AdminPartners />} />
                  <Route path="showcase" element={<AdminShowcase />} />
                  <Route path="videos" element={<AdminVideos />} />
                  <Route path="prospects" element={<AdminProspects />} />
                  <Route path="sales/pricing" element={<AdminSalesPricing />} />
                  <Route path="members" element={<AdminMembers />} />
                  <Route path="organisations" element={<AdminOrganisations />} />
                  <Route path="ndas" element={<AdminNdas />} />
                  <Route path="invoices" element={<AdminInvoices />} />
                  <Route path="abandoned" element={<AdminAbandoned />} />
                  <Route path="ebooks" element={<AdminEbooks />} />
                  <Route path="security" element={<AdminSecurity />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
                <Route path="/portal/login" element={<PortalLogin />} />
                <Route path="/portal" element={<PortalRoute><PortalLayout /></PortalRoute>}>
                  <Route index element={<Navigate to="/portal/dashboard" replace />} />
                  <Route path="dashboard" element={<PortalDashboard />} />
                  <Route path="deals" element={<PortalDeals />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            <Footer />
          </div>
          <Toaster position="top-center" richColors />
          <CookieConsent />
          <SocialProof />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
